/** Part III — Things you own and make: inventory and crafting, vehicles, homes and furniture, droids, building. */
import type { HelpPart } from './types.js';

export const PART_MAKING: HelpPart = {
  numeral: 'III',
  title: 'Things you own and make',
  chapters: [
    {
      id: 'inventory',
      title: 'Inventory and crafting',
      blurb: 'Your things, resources, the Workbench, schematics and mining rigs.',
      pages: [
        {
          id: 'inventory/inventory',
          title: 'The inventory',
          terms: ['f1', 'items', 'bag', 'equip', 'deeds', 'hangar'],
          body: `<p><kbd>F1</kbd> (or <b>Inventory</b> on the menu bar) opens your inventory on the Workbench window. It
          lists:</p>
          <ul>
          <li><b>Items</b> — weapons, tools, vehicles, furniture, droids. Each has a quality and a serial number, and
          remembers who crafted it. <b>Equip</b>, <b>Ride</b>, <b>Place here</b> or <b>Deploy</b> as the item allows.</li>
          <li><b>Resources</b> — stacks of raw and refined materials, each with its own rolled stats.</li>
          <li><b>Deeds</b> — packed buildings and rigs, ready to place.</li>
          <li><b>Ships</b> — your hangar; mark one active.</li>
          <li><b>Schematics</b> — the complex designs you hold, with a <b>Craft</b> shortcut.</li>
          </ul>
          <p>Something offered in an open trade, stored in a container or pledged at the bank is not in your inventory
          until it comes back.</p>`,
          see: ['inventory/crafting', 'groups/trade'],
        },
        {
          id: 'inventory/resources',
          title: 'Resources and surveying',
          terms: ['ore', 'sample', 'survey', 'hotspot', 'concentration', 'stats', 'quality'],
          body: `<p>Resources come in families — ores, crystals, fibres, timber, grain, game and more — and every stack has
          its own stats, which carry into what you craft from it.</p>
          <p>The Workbench (<kbd>F6</kbd>) <b>Survey</b> tab maps where a resource runs rich around you. Walk to a hotspot
          and hand-sample it; an equipped <b>field tool</b> yields more per sample. For steady supply, place a
          <a data-page="inventory/rigs">mining rig</a> on the richest ground you found. A droid's scan maps
          concentrations too.</p>`,
        },
        {
          id: 'inventory/crafting',
          title: 'Crafting at the Workbench',
          terms: ['craft', 'workbench', 'f6', 'components', 'recipe', 'slots'],
          body: `<p><kbd>F6</kbd> opens the Workbench. Pick a design on the <b>Craft</b> tab and fill its slots: each slot
          takes a quantity of one resource family, or a component you made earlier. A slot draws from a single stack,
          so keep good stacks together.</p>
          <p>Designs build on each other: raw materials become <b>components</b> (ingots, plates, weaves, cells, coils,
          lattices…), and components become weapons, tools, vehicles, furniture, droids, rig deeds and ship parts. When
          a slot is short, the bench offers “Make” for the missing component.</p>
          <p>The quality of what you craft follows the quality of what you put in.</p>`,
          see: ['inventory/guide', 'inventory/schematics'],
        },
        {
          id: 'inventory/guide',
          title: 'The Crafting Guide',
          terms: ['f10', 'recipes', 'ready', 'missing', 'locked'],
          body: `<p><kbd>F10</kbd> (or <b>Recipes</b>) lists every design in the game with its full recipe, and judges each
          against what you hold right now:</p>
          <ul>
          <li><b>READY</b> — you can craft it; <b>Craft</b> opens the bench on it.</li>
          <li><b>MISSING</b> — the red slots say what to gather or make first, one level down.</li>
          <li><b>LOCKED</b> — you need its schematic; <b>Dealers</b> shows where to buy one.</li>
          </ul>
          <p>Filter by category, by “craftable now” or “designs I hold”, or search by name.</p>`,
        },
        {
          id: 'inventory/schematics',
          title: 'Schematics, dealers and reverse engineering',
          terms: ['schematic', 'dealer', 'reverse engineer', 'complex designs'],
          body: `<p><b>Basic work is open to everyone</b>: every component and every item up to complexity 3. <b>Complex
          designs</b> — rig cores, ship components, droids, the higher-tier weapons, the faster skimmers, industrial rigs
          — and <b>every ship hull</b> need a <b>schematic</b>, which you keep for good once you have it.</p>
          <ul>
          <li><b>Buy one from a dealer</b> — the smith (or merchant) of every city and town keeps a seasonal board of
          designs; it turns every six hours. Workbench → Market, within 400 m of the town square. The dealer keeps the
          credits.</li>
          <li><b>Reverse-engineer</b> a finished piece or a hull you own (Inventory → Reverse engineer). The item is
          destroyed and the design is yours. A design you already hold is refused, so nothing is wasted.</li>
          </ul>
          <p>Ship hulls crafted at the bench land in your hangar.</p>`,
        },
        {
          id: 'inventory/rigs',
          title: 'Mining rigs',
          terms: ['extractor', 'rig', 'hopper', 'maintenance', 'rig core', 'redeed'],
          body: `<p>Extractors are manufactured. The Workbench lists the <b>Field</b>, <b>Industrial</b> and <b>Heavy
          Extractor Rig</b>; crafting one makes a packed <b>deed</b>, with the craft's quality banked as maintenance.</p>
          <p>Place the deed on open ground with a free lot, then work it from <kbd>F6</kbd> → <b>Rigs</b>: yield follows
          the resource concentration under the rig, so survey first. Keep its maintenance pool fed, empty its hopper,
          slot an <b>Extraction Rig Core</b> to upgrade it, and <b>redeed</b> to pack it up and move it. Rig deeds trade
          on the bazaar like anything else; a droid can run the hopper for you.</p>`,
          see: ['homes/placing', 'droids/classes'],
        },
      ],
    },
    {
      id: 'vehicles',
      title: 'Vehicles',
      blurb: 'Hovercraft, your ship in the sky, landing and crashing, and the Grand Circuit.',
      pages: [
        {
          id: 'vehicles/hovercraft',
          title: 'Hovercraft',
          terms: ['skimmer', 'dust skiff', 'ride', 'x', 'mount', 'throttle', 'boost'],
          body: `<p>Hovercraft are crafted: <a data-page="vehicles/models">fifteen models</a> across five tiers, from the Dust Skiff to the Storm
          Tempest. Speed rises with tier, and craft quality raises it further. They trade on the bazaar like any crafted good.</p>
          <p><kbd>X</kbd> in the open (or <b>Ride</b> in the inventory) steps aboard; <kbd>X</kbd> again steps off.</p>
          <table>
          <tr><td>{{key:throttleUp}} / {{key:throttleDown}}</td><td>Throttle / brake and reverse</td></tr>
          <tr><td>{{key:steerLeft}} / {{key:steerRight}} or the mouse</td><td>Steer — the mouse turns the wheel, so the craft turns at the speed it can</td></tr>
          <tr><td>{{key:boost}}</td><td>Boost</td></tr>
          <tr><td>{{key:handbrake}}</td><td>Handbrake — kick the tail out to drift</td></tr>
          <tr><td>Mouse wheel</td><td>Chase camera distance</td></tr>
          </table>
          <p>It skims terrain and open water alike, rocks on the swell, throws spray and leaves a wake.</p>`,
          see: ['vehicles/handling', 'vehicles/racing', 'water/craft'],
        },
        {
          id: 'vehicles/handling',
          title: 'Handling: drift, walls and jumps',
          terms: ['drift', 'wall', 'scrape', 'ramp', 'jump', 'handling'],
          body: `<ul>
          <li><b>Grip and drift</b> — push a hard turn at speed and the tail steps out into a drift; the HUD shows when
          you are sliding. Ease off and it gathers itself.</li>
          <li><b>Walls</b> — a glancing touch costs a little speed and you slide along; a hard hit bounces you off and
          turns the nose away. You can always steer away from a wall, even stopped against it.</li>
          <li><b>Ramps</b> — the craft follows the ground closely; leave a crest fast and you fly, landing with a
          splash on water.</li>
          </ul>`,
        },
        {
          id: 'vehicles/models',
          title: 'The fifteen hovercraft',
          terms: ['dust skiff', 'pack mule', 'reed punt', 'dune runner', 'fen strider', 'road lark', 'sky falcon', 'canyon wasp', 'iron ox', 'ridge raptor', 'glass needle', 'trail marshal', 'storm tempest', 'gilt courser', 'winter kite', 'tier'],
          body: `<p>Three craft in each of five tiers. Within a tier they differ in how fast they wake up, how hard the
          boost kicks and how high you sit; a craft of a higher tier always cruises faster than one of a lower tier.
          Tiers 1 and 2 are open work at any workshop; from tier 3 you need the design — bought from a schematic
          dealer, or learned by taking one apart.</p>
          <table>
          <tr><td><b>Tier 1</b></td><td><b>Dust Skiff</b> — the starter. <b>Pack Mule</b> — a cargo sled: slow to get going, with the smallest boost. <b>Reed Punt</b> — a marsh canoe, the quickest of the three off the mark.</td></tr>
          <tr><td><b>Tier 2</b></td><td><b>Dune Runner</b> — the workhorse. <b>Fen Strider</b> — twin floats under a railed deck; you ride the helm high above them. <b>Road Lark</b> — an open two-seat tourer (you take the driver's seat; nobody rides beside you).</td></tr>
          <tr><td><b>Tier 3</b></td><td><b>Sky Falcon</b> — the courier skimmer. <b>Canyon Wasp</b> — a twin-boom racer, the fastest craft of all to reach its cruise. <b>Iron Ox</b> — an armoured hauler that takes its time.</td></tr>
          <tr><td><b>Tier 4</b></td><td><b>Ridge Raptor</b> — twin coils. <b>Glass Needle</b> — a dart with the largest boost over its cruise, and a long spool. <b>Trail Marshal</b> — the militia's patrol skimmer, quick and steady.</td></tr>
          <tr><td><b>Tier 5</b></td><td><b>Storm Tempest</b> — still the fastest. <b>Gilt Courser</b> — an Aurelian parade courser. <b>Winter Kite</b> — a Borean racing skimmer. All three need a storm core.</td></tr>
          </table>
          <p>Every hovercraft carries one rider and handles by the same rules — grip, drift and steering are not per
          model. On the ten newer craft you sit in the craft's own seat, and the ride height follows the seat: a Fen
          Strider's helm stands well above a Canyon Wasp's.</p>`,
          see: ['vehicles/hovercraft', 'vehicles/handling', 'inventory/schematics'],
        },
        {
          id: 'vehicles/ship',
          title: 'Your ship in the sky',
          terms: ['ship', 'atmosphere', 'flight', 'shift x', 'recall', 'overcruise'],
          body: `<p>Your active ship flies in the Commons' sky too. <kbd>Shift</kbd>+<kbd>X</kbd> recalls it: it sets down
          on clear ground beside you (never inside a settlement, never on water). <kbd>X</kbd> beside the hull boards and
          lifts off.</p>
          <table>
          <tr><td>{{key:throttleUp}} / {{key:throttleDown}}</td><td>Set a held throttle</td></tr>
          <tr><td>Mouse</td><td>Steer the nose</td></tr>
          <tr><td>{{key:rollLeft}} / {{key:rollRight}}</td><td>Roll</td></tr>
          <tr><td>{{key:levelOut}}</td><td>Level the wings</td></tr>
          <tr><td>{{key:boost}}</td><td>Booster</td></tr>
          <tr><td>{{key:fire}} or click</td><td>Fire the cannons (capacitor-fed)</td></tr>
          </table>
          <p>A steady full throttle winds up <b>overcruise</b>. It flies with your ship's real stats — its chassis and
          fitted parts. Strafing runs hurt creatures of the wilds, loot and cull contracts included. Keep climbing and the
          sky thins around 1,200 m and hands you to space.</p>
          <p>Low over a lake or the sea the engines blow the water flat beneath you, and the ship skims the surface
          rather than diving under it — see <a data-page="water/craft">Craft over the water</a>.</p>`,
          see: ['vehicles/landing', 'water/craft', 'space/reach'],
        },
        {
          id: 'vehicles/landing',
          title: 'Landing, parking and crashing',
          terms: ['land', 'park', 'crash', 'recovery fee'],
          body: `<p><kbd>X</kbd> low over lawful ground lands. The ship then stays parked where you left it, visible to
          everyone. City airspace refuses landings — use the pads.</p>
          <p>The ground and settlement skylines are solid. Hard impacts eat shield, then hull; a breached hull
          <b>crashes</b>, and the wreck settles on the nearest lawful ground for a recovery fee.</p>`,
        },
        {
          id: 'vehicles/racing',
          title: 'The Grand Circuit',
          terms: ['race', 'racing', 'grand circuit', 'leaderboard', 'season', 'lap'],
          body: `<p>A 31 km hovercraft loop: from the stadium campus, along the Aurelian capital's wall, over a ridge and its
          switchbacks, along the Borean wall and home across open country. Barriers line it and gantries mark its twelve
          sectors.</p>
          <ul>
          <li><b>Races</b> run every 30 minutes. Join from the Town window's <b>Races</b> tab.</li>
          <li>Be <b>riding within 220 m of the start line</b> when the lights go out, or you miss it. Up to twelve
          players race; named rivals fill the grid to eight.</li>
          <li>Pass the gates in order. The HUD shows your place, the next gate and your sector times.</li>
          <li>Points are awarded like a championship (25 for a win down to 1) into a <b>seven-day season</b>; the
          standings and recent results live in the Races tab.</li>
          </ul>
          <p>Outside a race, ride the track any time: a lap timer starts on the line and keeps your best.</p>`,
        },
      ],
    },
    {
      id: 'homes',
      title: 'Homes and buildings',
      blurb: 'Deeds and lots, placing a building, its console, going inside, furnishing, and storage.',
      pages: [
        {
          id: 'homes/deeds',
          title: 'Deeds and lots',
          terms: ['deed', 'lot', 'house', 'structure', 'building'],
          body: `<p>Every building you own begins as a <b>deed</b> — crafted at the Workbench, bought on the bazaar, or
          packed up from a building you already had. There are houses (small to large), a manor, a hotel, stores,
          markets, a garage, a cantina, a museum, a medical centre, a salon, factories and mining facilities, gardens,
          statues, fountains, lamps, walls, turrets, and the civic pieces a city needs.</p>
          <p>Buildings take <b>lots</b> from your allowance; a bigger building takes more. Each building also has a
          maintenance pool you keep topped up.</p>`,
          see: ['homes/placing', 'groups/cities'],
        },
        {
          id: 'homes/placing',
          title: 'Placing a building',
          terms: ['placement', 'green', 'red', 'yellow', 'rotate', 'outline'],
          body: `<p><b>Place</b> a deed from the inventory and the view lifts to a map straight down over you, with the
          building's outline under the cursor. Walk to move the map; the outline follows the cursor.</p>
          <ul>
          <li><b>Green</b> — you can place it here. Click.</li>
          <li><b>Yellow</b> — allowed in principle but not here (a city rule, someone's land).</li>
          <li><b>Red</b> — not allowed: too steep, in water, overlapping another building, or its door would face a
          wall or the water.</li>
          </ul>
          <p>Right-click turns it 90°, <kbd>R</kbd> 45°, the wheel 15°. The white triangle marks the door; the ring in
          front of it must stay clear — including of other people's doors. The status line says exactly why a spot is
          refused. <kbd>Esc</kbd> leaves the map.</p>`,
        },
        {
          id: 'homes/console',
          title: 'The structure console',
          terms: ['console', 'rename', 'private', 'access list', 'ban', 'maintenance', 'redeed'],
          body: `<p>Inside your building, a console stands at the back wall. {{key:interact}} at it opens the building's
          management:</p>
          <ul>
          <li><b>Name</b> — what the door and the maps call it.</li>
          <li><b>Entry</b> — public (anyone may come in) or private.</li>
          <li><b>Access</b> — add players by username as <b>admin</b> (helps manage it), <b>entry</b> (may come in when
          private) or <b>banned</b> (never).</li>
          <li><b>Maintenance</b> — the pool, the building's condition, and paying in.</li>
          <li><b>Furnishing</b> — the furnish packs (next pages).</li>
          <li><b>Redeed</b> — pack the building back into a deed. You confirm twice; storage must be empty first.
          Crafted furniture still standing comes back to your inventory.</li>
          </ul>`,
        },
        {
          id: 'homes/inside',
          title: 'Going inside',
          terms: ['interior', 'door', 'enter', 'floors', 'stairs'],
          body: `<p>Walk to the door of a building that has an interior and press {{key:interact}}. Homes, civic halls,
          stores, the large halls of the cities, your own houses and guild halls all have one. Tall buildings carry several
          floors; stairs climb one wall. Windows are real openings onto the street outside.</p>
          <p>{{key:interact}} at the door steps back out. Industrial buildings, rigs and small props have no inside.
          Inside, the weather stays out and storms cannot reach you.</p>`,
        },
        {
          id: 'homes/packs',
          title: 'Furnish packs',
          terms: ['furnish pack', 'furnished', 'essentials', 'grand'],
          body: `<p>Your building starts empty. At its console you can buy a <b>furnish pack</b> that fills the rooms with a
          matching set:</p>
          <table>
          <tr><td>1 Essentials</td><td>300 ⓒ</td><td>the basics, ground floor</td></tr>
          <tr><td>2 Comfort</td><td>700 ⓒ</td><td>more, two floors</td></tr>
          <tr><td>3 Furnished</td><td>1,400 ⓒ</td><td>every floor</td></tr>
          <tr><td>4 Appointed</td><td>2,600 ⓒ</td><td>fuller</td></tr>
          <tr><td>5 Grand</td><td>4,500 ⓒ</td><td>everything</td></tr>
          </table>
          <p>Each pack is the one below it with more added. Remove your own placed items first; clearing a pack is free
          (no refund). The console shows how many pieces each pack would place in this building.</p>`,
        },
        {
          id: 'homes/furniture',
          title: 'Crafted furniture',
          terms: ['furniture', 'place here', 'pick up', 'bed', 'sofa', 'table'],
          body: `<p>Forty-odd pieces — beds, sofas, tables, wardrobes, lamps, planters, kitchens, chests, a hover chair, an
          orchestrion, a guest book — are crafted like any item. Stand inside a building you own (or administer) and press
          <b>Place here</b> on the piece: it drops at your feet, facing your way. Press <kbd>F</kbd> beside a placed piece
          to take it back, quality and serial intact. You cannot furnish buildings you do not own.</p>`,
          see: ['homes/functions'],
        },
        {
          id: 'homes/functions',
          title: 'What furniture does',
          terms: ['sit', 'lie down', 'rest', 'lamp', 'orchestrion', 'planter', 'guest book', 'mirror'],
          body: `<p>Walk up to a piece and press {{key:interact}}:</p>
          <ul>
          <li><b>Seats</b> sit you down; <b>beds</b> lay you down — resting in a bed lifts your Rest need. Any movement
          key stands you up.</li>
          <li><b>Lamps and braziers</b> switch on and off.</li>
          <li>The <b>orchestrion</b> plays music for the room.</li>
          <li>A <b>planter</b> takes a seed of any food and grows it; harvest after four hours for three times as much.</li>
          <li>The <b>guest book</b> takes a line from any visitor and keeps the last forty.</li>
          <li>The <b>mirror</b> opens your Passport; a <b>workbench</b> opens the Workbench; a <b>terminal</b> the market.</li>
          <li><b>Storage</b> pieces open your storage (next page).</li>
          </ul>`,
        },
        {
          id: 'homes/storage',
          title: 'Storage',
          terms: ['chest', 'crate', 'wardrobe', 'safe', 'weapon rack', 'store', 'container'],
          body: `<p>Chests, crates, wardrobes, safes, cabinets, shelves, coolers, bookcases, kitchens and weapon racks hold
          your things. Only the building's <b>owner</b> can open them, store, take, or even see what is inside.</p>
          <p>Capacity by piece: crate 36, chest 24, wardrobe 16, safe, cabinet, shelf and cooler 12, bookcase 10, kitchen 8,
          weapon rack 4 (weapons only). Storing moves an item out of your inventory; stacks can be split. You must be at
          the building to use it.</p>
          <p>A piece with something in it cannot be picked up, and a building with full storage cannot be redeeded —
          empty it first.</p>`,
        },
        {
          id: 'homes/personal',
          title: 'Your personal world and more worlds',
          terms: ['personal world', 'my worlds', 'f8', 'visibility', 'guest list'],
          body: `<p>Your personal world is 64 × 64 m and entirely yours — build without any permission. {{key:map}}
          swaps you between it and the Commons.</p>
          <p><kbd>F8</kbd> (<b>Worlds</b>) creates more worlds: name each, pick a size and a visibility (<b>public</b> —
          listed in discovery; <b>unlisted</b> — by link; <b>private</b>), and grant other players roles by username
          (build, decorate, script). Hosting a world has a one-time fee in credits.</p>`,
          see: ['building/editmode'],
        },
      ],
    },
    {
      id: 'droids',
      title: 'Droids',
      blurb: 'Six classes of droid, how to deploy and command them, programming, and running costs.',
      pages: [
        {
          id: 'droids/classes',
          title: 'The six droids',
          terms: ['battle droid', 'supply droid', 'medical droid', 'humanoid', 'ship droid', 'mega'],
          body: `<ul>
          <li><b>Battle Droid</b> — guards, patrols and fights wildlife.</li>
          <li><b>Supply Droid</b> — a four-legged hauler with a big cargo bed: extractor runs, storage, organising, a
          vendor stall. Anyone can craft one.</li>
          <li><b>Medical Droid</b> — heals you, other travelers, townsfolk and other droids.</li>
          <li><b>Humanoid Droid</b> — a little of every trade; the only one that crafts components.</li>
          <li><b>Ship Droid</b> — tends your active ship's hold, fittings and status; docked at a ship's droid port it
          repairs the hull faster.</li>
          <li><b>Mega Battle Droid</b> — tank-sized, splash damage, heavy armour, one at a time.</li>
          </ul>
          <p>The complex ones need their schematic from a dealer.</p>`,
        },
        {
          id: 'droids/command',
          title: 'Deploying and commanding',
          terms: ['deploy', 'k', 'terminal', 'datapad', 'recall', 'vendor'],
          body: `<p><b>Droids</b> on the menu bar is your datapad. Deploy from it or from the inventory, on the Commons;
          three at once. <kbd>K</kbd> at your droid opens its terminal; <kbd>K</kbd> at someone else's droid with prices
          on its cargo opens its <b>stall</b>.</p>
          <p>Orders, upkeep and reports work from anywhere; uploading a program, loading cargo and repairs need you
          beside the droid — if you are too far, “Call it here” brings it to you. A droid acts for you: it collects
          from your extractors, stores into your furniture, sells and posts on the bazaar in your name, pays upkeep on
          your buildings (capped) — and never strikes a player or a townsperson. <b>Recall</b> packs it back into your
          bag with its cargo and unspent credits.</p>`,
        },
        {
          id: 'droids/script',
          title: 'Programming droids',
          terms: ['droidscript', 'program', 'patrol', 'handlers', 'sensors'],
          body: `<p>A droid runs <b>DroidScript</b>. Simple programs are one command per line — <code>patrol 40</code>,
          <code>scan life</code>, <code>wait 20</code>, <code>loop</code>. There is more when you want it: variables
          (<code>let</code>), maths, <code>if / elif / else / end</code>, <code>while</code>, <code>repeat 5</code>,
          <code>forever</code>, <code>for x in list</code>, functions (<code>def … end</code>) and <b>handlers</b> that
          interrupt the main program (<code>on creature_near 30</code>, <code>on sale</code>, <code>every 60</code>).</p>
          <p>Sensors read the world (<code>creature(30)</code>, <code>extractor(500)</code>, <code>cargo(ore)</code>,
          <code>injured(20)</code>, <code>town()</code>…) and commands act (<code>collect</code>, <code>store all</code>,
          <code>craft metal_ingot 3</code>, <code>survey ferrite_ore 250 50</code>, <code>sell ore</code>,
          <code>post ore 300 50</code>, <code>attack weakest</code>, <code>heal any</code>, <code>upkeep 100</code>…).
          The Program tab has prebuilt programs for every class, checks your code as you type and lists the whole API.</p>`,
        },
        {
          id: 'droids/costs',
          title: 'Running costs and the estimator',
          terms: ['upkeep', 'credits pool', 'estimator', 'broke'],
          body: `<p>A droid runs on credits you fund into its pool: an hourly running cost while it works, plus a price for each
          action — harder actions cost more, and bigger classes multiply them. An empty pool parks the program; fund it
          and it carries on where it stopped.</p>
          <p>The <b>Estimator</b> tab prices any program on any class: per hour, per pass, how long a pool lasts, and where
          every credit goes.</p>`,
        },
      ],
    },
    {
      id: 'building',
      title: 'Creating in your worlds',
      blurb: 'Edit Mode, scripting, world logic, and who may do what in a world.',
      pages: [
        {
          id: 'building/editmode',
          title: 'Edit Mode',
          terms: ['f2', 'edit mode', 'sculpt', 'paint', 'gizmo', 'prefab'],
          body: `<p>Press <kbd>F2</kbd> (<b>Edit</b>) in a world you own or hold a build role in. You fly (WASD, Space up,
          Shift faster; hold the right mouse button to look), the cursor picks things, and every tool sits on one
          toolbar:</p>
          <table>
          <tr><td><kbd>1</kbd></td><td><b>Select</b> — Delete removes, Ctrl+D duplicates, Ctrl+Z / Ctrl+Y undo and redo</td></tr>
          <tr><td><kbd>2</kbd> <kbd>3</kbd> <kbd>4</kbd></td><td>Move, rotate, scale; <kbd>X</kbd> toggles snapping</td></tr>
          <tr><td><kbd>5</kbd></td><td><b>Place</b> — shapes, prefabs, your uploads, your deeds; <kbd>R</kbd> rotates</td></tr>
          <tr><td><kbd>6</kbd></td><td><b>Sculpt</b> terrain — raise, lower (Ctrl), flatten (Alt)</td></tr>
          <tr><td><kbd>7</kbd></td><td><b>Paint</b> the ground</td></tr>
          <tr><td><kbd>8</kbd></td><td><b>Interior</b> — make a building enterable</td></tr>
          <tr><td><kbd>9</kbd></td><td><b>Script</b> — what happens when someone uses a part</td></tr>
          <tr><td><kbd>0</kbd></td><td><b>Upload</b> a model</td></tr>
          <tr><td><kbd>-</kbd></td><td><b>NPC</b> — place a scripted character</td></tr>
          <tr><td><kbd>=</kbd></td><td><b>Versona</b> — place a living townsperson</td></tr>
          <tr><td><kbd>[</kbd></td><td><b>Environment</b> — time, weather, fog, light, ambience for the world</td></tr>
          <tr><td><kbd>]</kbd></td><td><b>Audio</b> zones</td></tr>
          <tr><td><kbd>;</kbd></td><td><b>Devices</b> — lights, doors, buttons, teleporters, seats, platforms, jump pads, timers, cinematics and more, wired by signals</td></tr>
          <tr><td><kbd>Esc</kbd></td><td>Deselect, then cancel, then back to exploring</td></tr>
          </table>
          <p>Shift-click or drag a box to select several things; the outliner lists everything placed, the inspector takes
          exact numbers, and <b>Save as prefab</b> keeps a selection for reuse. Collaborative building happens here too:
          <kbd>G</kbd> inside Edit Mode opens the shared canvas. Everything is checked by the server.</p>`,
          see: ['building/scripts', 'building/permissions'],
        },
        {
          id: 'building/scripts',
          title: 'Scripting: the Lua IDE',
          terms: ['lua', 'ide', 'script', 'copilot', 'contract', 'world script', 'npc script'],
          body: `<p>Inside Edit Mode, <kbd>I</kbd> opens the <b>Lua IDE</b>. It holds three kinds of script:</p>
          <ul>
          <li><b>Server contracts</b> — compiled and run on the server, sandboxed, using only the capabilities the world's
          owner granted, within a time and memory budget.</li>
          <li><b>World scripts</b> — Lua every visitor's game runs: props, motion, labels, particles, sounds, reactions to
          contract events.</li>
          <li><b>NPC scripts</b> — one per scripted character, written against the <code>npc.*</code> API: what it senses,
          says, where it walks, what it remembers about each player.</li>
          </ul>
          <p><b>Deploy</b> (Ctrl+S) publishes; <b>▶ Run</b> previews. The API panel lists every function with
          documentation (searchable), and a built-in copilot can read your scripts, run them in the sandbox with
          simulated events, and propose changes you review before deploying.</p>`,
        },
        {
          id: 'building/logic',
          title: 'World Logic',
          terms: ['world logic', 'visual scripting', 'nodes', 'l'],
          body: `<p><kbd>L</kbd> opens <b>World Logic</b>: build behaviour from nodes — a door, a trivia game, a timer, a
          spawner — without writing code. It compiles to the same signed, sandboxed contract the Lua IDE writes; the
          owner grants it capabilities, and a contract that runs too heavy is throttled, never deleted.</p>`,
        },
        {
          id: 'building/permissions',
          title: 'Who may do what in a world',
          terms: ['permissions', 'roles', 'build', 'decorate', 'script', 'admin', 'visitor'],
          body: `<p>Rights in a world are roles per person:</p>
          <table>
          <tr><td><b>visitor</b></td><td>may enter (everyone, by default)</td></tr>
          <tr><td><b>build</b></td><td>may shape terrain and place things</td></tr>
          <tr><td><b>decorate</b></td><td>may place and move decorative things</td></tr>
          <tr><td><b>script</b></td><td>may write world logic</td></tr>
          <tr><td><b>admin</b></td><td>everything, including granting roles</td></tr>
          </table>
          <p>A world's owner holds every right. The Commons belongs to the platform, so nobody owns it.</p>`,
        },
        {
          id: 'building/provenance',
          title: 'Item permissions and remixing',
          terms: ['provenance', 'copy', 'modify', 'transfer', 'remix', 'template', 'creator'],
          body: `<p>Things you create carry five permissions the server enforces everywhere: <b>copy</b>, <b>modify</b>,
          <b>transfer</b>, <b>remix</b> (make a new work from it) and <b>template</b> (instantiate copies). The creator is
          recorded forever; a remix is a new item linked to its parent. When you pass something on, the new owner gets only
          what the item allows <i>and</i> what you grant.</p>`,
        },
      ],
    },
  ],
};
